import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { Button, Modal, Select } from 'antd';

import { convertLuoxData } from 'utils';

const { Option } = Select;

export const AlphaOpicPlot = () => {
  const [showInfo, setInfoModal] = useState(false);
  const [plotType, setPlotType] = useState('radiance');

  const luoxData = useSelector((state) => state.dataReducers.luoxData);
  const luoxUnits = useSelector((state) => state.dataReducers.luoxUnits);

  const radianceOrIrradiance =
    luoxUnits.radianceOrIrradiance.charAt(0).toUpperCase() +
    luoxUnits.radianceOrIrradiance.slice(1);

  const standardLuoxData = convertLuoxData(luoxData.luoxInput, {
    absoluteOrRelative: 'absolute',
    areaScale: 'm²',
    powerScale: 'mW',
    radianceOrIrradiance: 'irradiance',
    relativePower: 1,
  });

  const convertedLuoxData = convertLuoxData(luoxData.luoxInput, luoxUnits);

  const endUnites = `${luoxUnits.powerScale}.${
    luoxUnits.areaScale === 'm²'
      ? 'm⁻²'
      : luoxUnits.areaScale === 'cm²'
      ? 'cm⁻²'
      : 'mm⁻²'
  }${luoxUnits.radianceOrIrradiance === 'radiance' ? '.sr' : ''}`;

  const InfoModal = (
    <Modal
      title="Local Color Fidelity"
      visible={showInfo}
      onCancel={() => setInfoModal(false)}
      footer={[
        <Button key="close" onClick={() => setInfoModal(false)}>
          Close
        </Button>,
      ]}
    >
      Info
    </Modal>
  );

  const handlePlotChange = (value) => {
    setPlotType(value);
  };

  let values = [];
  let labels = [];
  let maxValue;
  let roundToFive;

  const powerScale = { µW: 1000000, mW: 1000, W: 1 };
  const areaScale = { 'mm²': 1000000, 'cm²': 10000, 'm²': 1 };

  switch (plotType) {
    case 'radiance':
      const { sConeTotals, mConeTotals, lConeTotals, rodTotals, melTotals } =
        standardLuoxData.luoxOutput;
      values = [
        sConeTotals[0],
        mConeTotals[0],
        lConeTotals[0],
        rodTotals[0],
        melTotals[0],
      ];

      maxValue = Math.round(Math.max(...values));

      for (let i = 0; i <= maxValue; i++) {
        labels.push(i);
      }

      values = [
        convertedLuoxData.luoxOutput.sConeTotals[0],
        convertedLuoxData.luoxOutput.mConeTotals[0],
        convertedLuoxData.luoxOutput.lConeTotals[0],
        convertedLuoxData.luoxOutput.rodTotals[0],
        convertedLuoxData.luoxOutput.melTotals[0],
      ];
      break;
    case 'edl':
      const { sc, mc, lc, rh, mel } =
        standardLuoxData.luoxOutput.equivalentDaylightAlphaOpic;
      values = [sc[0], mc[0], lc[0], rh[0], mel[0]];

      maxValue = Math.round(Math.max(...values));

      for (let i = 0; i <= maxValue; i++) {
        labels.push(i);
      }

      values = [
        convertedLuoxData.luoxOutput.equivalentDaylightAlphaOpic.sc[0],
        convertedLuoxData.luoxOutput.equivalentDaylightAlphaOpic.mc[0],
        convertedLuoxData.luoxOutput.equivalentDaylightAlphaOpic.lc[0],
        convertedLuoxData.luoxOutput.equivalentDaylightAlphaOpic.rh[0],
        convertedLuoxData.luoxOutput.equivalentDaylightAlphaOpic.mel[0],
      ];
      break;
    case 'elr':
      const {
        sc: scElr,
        mc: mcElr,
        lc: lcElr,
        rh: rhElr,
        mel: melElr,
      } = standardLuoxData.luoxOutput.alphaOpicEfficiency;
      values = [scElr[0], mcElr[0], lcElr[0], rhElr[0], melElr[0]];

      maxValue = Math.round(Math.max(...values));
      roundToFive = Math.ceil(maxValue / 5) * 5;

      for (let i = 0; i <= roundToFive; i++) {
        labels.push(i);
      }
      break;
    default:
      break;
  }

  if (plotType !== 'elr') {
    labels = labels.map(
      (value) =>
        (value * areaScale[luoxUnits.areaScale]) /
        powerScale[luoxUnits.powerScale]
    );
  }

  return (
    <div>
      <Plot
        useResizeHandler={true}
        style={{ paddingLeft: '6px' }}
        title={null}
        data={[
          {
            name: '',
            type: 'bar',
            x: labels,
            y: values,
            marker: {
              color: '#d77600',
            },
            textfont: {
              size: 14,
            },
            hovertemplate: '%{y:.4f}',
          },
        ]}
        layout={{
          width: 500,
          height: 250,
          margin: {
            l: 64,
            r: 40,
            b: 45,
            t: 10,
            pad: 0,
          },
          xaxis: {
            tickmode: 'array',
            tickvals: labels,
            ticktext: ['S-cone', 'M-cone', 'L-cone', 'Rhodopic', 'Melanopic'],
            fixedrange: true,
            mirror: 'ticks',
            linewidth: 2,
            linecolor: 'black',
          },
          yaxis: {
            showline: true,
            range: [0, roundToFive],
            title: {
              text:
                plotType === 'radiance'
                  ? `Alpha-Opic ${radianceOrIrradiance} (${endUnites})`
                  : plotType === 'edl'
                  ? `S-cone-opic ED${
                      radianceOrIrradiance === 'Radiance' ? 'L' : 'I'
                    } [${radianceOrIrradiance === 'Radiance' ? 'cd/m²' : 'lx'}]`
                  : `S-cone-opic ELR [${
                      radianceOrIrradiance === 'Radiance' ? 'cd/m²' : 'lx'
                    }]`,
              font: {
                size: 12,
              },
            },
            tickformat: '',
            fixedrange: true,
            // ticklen: 6,
            // tickwidth: 2,
            mirror: 'ticks',
            linewidth: 2,
            linecolor: 'black',
          },
          bargap: 5,
        }}
        config={{
          displaylogo: false,
          modeBarButtons: [
            [
              'toImage',
              {
                name: 'Info',
                icon: '',
                click: () => setInfoModal(true),
              },
            ],
          ],
          responsive: true,
          toImageButtonOptions: {
            format: 'png', // one of png, svg, jpeg, webp
            filename: 'Local Color Fidelity',
            height: 250,
            width: 500,
            scale: 1.5, // Multiply title/legend/axis/canvas sizes by this factor
          },
        }}
      />
      <label>Plot Type:</label>{' '}
      <Select defaultValue={plotType} onChange={handlePlotChange}>
        <Option value="radiance">{`Alpha-Opic ${radianceOrIrradiance}`}</Option>
        <Option value="edl">{`Alpha-Opic ED${
          radianceOrIrradiance === 'Radiance' ? 'L' : 'I'
        }`}</Option>
        <Option value="elr">Alpha-Opic ELR</Option>
      </Select>
      {InfoModal}
    </div>
  );
};
